'use client';
import { GroqImage } from '@/helpers/Image';
import { useIsMobile } from '@/hooks/useIsMobile';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import { motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';

interface IPhilosophy {
  label: string;
  name: string;
  title: PortableTextBlock[];
  description: PortableTextBlock[];
  image: GROQImage;
  button: GROQButton;
  buttonMobile: GROQButton;
}

export default function Philosophy({
  label,
  name,
  title,
  description,
  image,
  button,
  buttonMobile,
}: IPhilosophy) {
  const isMobile = useIsMobile();

  return (
    <section id="philosophy-section" className="flex flex-col gap-[13.25rem] lg:gap-[26rem] translate-y-[.125rem] lg:-translate-y-0 pt-[4rem] pb-[12rem] lg:pt-[24.75rem] overflow-x-clip bg-[linear-gradient(to_bottom,black_87%,#F2F0EF_87%)] lg:bg-[linear-gradient(to_bottom,black_56%,#F2F0EF_56%)]">
      <div className="container max-w-[190rem]">
        <motion.div
          initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
          whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
          transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
          className="flex flex-wrap w-full justify-between items-end gap-[4rem] lg:gap-[18rem]"
        >
          <motion.div className="flex lg:w-[90rem] flex-col gap-[3rem] lg:gap-[2rem] uppercase text-offwhite lg:prose-h2:leading-[120%] prose-h2:leading-[110%] text-center lg:text-left">
            <p className="font-nunito text-[1.625rem] font-[300] tracking-[0.53625rem] flex flex-col lg:flex-row items-center gap-[1rem] lg:gap-[2rem]">
              {label}
              <span className="block h-[.125rem] bg-offwhite w-[6rem] lg:w-[7rem]"></span>
              {name}
            </p>
            {title && (
              <PortableText value={title} components={richTextComponent} />
            )}
          </motion.div>

          <motion.div className="lg:w-[59rem] text-[#f2f0ef]/70 flex flex-col gap-[4rem] lg:gap-[2rem] items-center lg:items-start text-center lg:text-left">
            {description && (
              <PortableText
                value={description}
                components={richTextComponent}
              />
            )}

            {button && <CTAButton data={button} className="hidden lg:block" />}
            {buttonMobile && <CTAButton data={buttonMobile} className="lg:hidden" />}
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        initial={{ y: isMobile ? '0' : '5rem' }}
        whileInView={{ y: 0 }}
        transition={{ duration: 3, ease: [0.25, 1, 0.5, 1] }}
        viewport={{ once: true }}
        className="w-full lg:w-full -translate-x-0 pointer-events-none select-none"
      >
        {image && (
          <GroqImage
            src={image}
            alt={image?.name}
            className="!w-full !h-full !object-contain scale-150 lg:scale-100"
          />
        )}
      </motion.div>
    </section>
  );
}
